import {
  Button,
  Flex,
  Grid,
  SelectField,
  TextField,
} from "@aws-amplify/ui-react";
import { useState } from "react";
import LocalizedStrings from "react-localization";
import { CModal, CModalBody, CModalHeader, CModalTitle } from "@coreui/react";
import SquareIcon from "@mui/icons-material/Square";
import moment from "moment";
import { HexColorPicker } from "react-colorful";

import useErrors from "hooks/useErrors";
import {
  configInitialValues,
  periods,
  scheduleTypes,
} from "../../config/constants";
import DateRangeForm from "components/utils/DateRangeForm";
import { globalStrings } from "config/translations/es";
import { ReplayCircleFilled } from "@mui/icons-material";
import CustomPopover from "components/utils/CustomPopover";

const ChartFiltersForm = ({
  onSubmit,
  onAutoRefreshAll,
  isAutoRefreshAll,
  initialValues = configInitialValues,
}) => {
  const [modalDateVisible, setModalDateVisible] = useState(false);
  const [chartFiltersFormState, setChartFiltersFormState] =
    useState(initialValues);
  const [errors, setErrors] = useState({});
  const validateErrors = useErrors({
    period: "isRequired",
    scheduleType: "isRequired",
    chartColor: "isRequired",
  });

  const onSubmitForm = () => {
    let hasErrors = false;

    Object.entries(chartFiltersFormState).forEach((itemForm) => {
      const [name, value] = itemForm;
      const check = !!checkErrors(name, value);
      if (!hasErrors) hasErrors = check;
    });

    if (!hasErrors) onSubmit(chartFiltersFormState);
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    if (name === "period" && value === periods[periods.length - 1]) {
      onOpenModalDate();
      return;
    } else if (name === "period") {
      setChartFiltersFormState((prevSt) => ({
        ...prevSt,
        dateFrom: "",
        dateTo: "",
      }));
    }
    setChartFiltersFormState((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const onChangeColor = (color) => {
    setChartFiltersFormState((prevSt) => ({
      ...prevSt,
      chartColor: color,
    }));
  };

  const onOpenModalDate = () => setModalDateVisible(true);

  const onSubmitDateRange = (from, to) => {
    setChartFiltersFormState((prevSt) => ({
      ...prevSt,
      period: periods[periods.length - 1],
      dateFrom: from,
      dateTo: to,
    }));
    setModalDateVisible(false);
  };

  const checkErrors = (name, value) => {
    const errorMessage = validateErrors(name, value);
    if (errorMessage)
      setErrors((prevErr) => ({ ...prevErr, [name]: errorMessage }));
    else setErrors((prevErr) => ({ ...prevErr, [name]: "" }));
    return errorMessage;
  };

  return (
    <>
      <Grid
        rowGap="15px"
        columnGap="15px"
        templateColumns={{ medium: "1fr 1fr 1fr 1fr" }}
      >
        <SelectField
          name="period"
          value={chartFiltersFormState.period}
          label={
            <span>
              {strings.fields.period.label}
              <span style={{ fontSize: "0.8rem", color: "red" }}>{" *"}</span>
            </span>
          }
          onChange={onChange}
          errorMessage={errors["period"]}
          hasError={!!errors["period"]}
        >
          {periods.map((val) => (
            <option value={val}>
              {globalStrings.dateItems[val]}{" "}
              {val === periods[periods.length - 1] &&
              !!chartFiltersFormState.dateFrom
                ? `${moment(chartFiltersFormState.dateFrom).format(
                    "YYYY/MM/DD"
                  )} - ${moment(chartFiltersFormState.dateTo).format(
                    "YYYY/MM/DD"
                  )}`
                : ""}
            </option>
          ))}
        </SelectField>
        <SelectField
          name="scheduleType"
          value={chartFiltersFormState.scheduleType}
          label={
            <span>
              {strings.fields.scheduleType.label}
              <span style={{ fontSize: "0.8rem", color: "red" }}>{" *"}</span>
            </span>
          }
          onChange={onChange}
          errorMessage={errors["scheduleType"]}
          hasError={!!errors["scheduleType"]}
        >
          {scheduleTypes.map((val) => (
            <option value={val}>{globalStrings.scheduleTypes[val]}</option>
          ))}
        </SelectField>
        <TextField
          name="chartColor"
          label={
            <span>
              {strings.fields.chartColor.label}
              <span style={{ fontSize: "0.8rem", color: "red" }}>{" *"}</span>
            </span>
          }
          value={chartFiltersFormState.chartColor}
          onChange={onChange}
          errorMessage={errors["chartColor"]}
          hasError={!!errors["chartColor"]}
          outerEndComponent={
            <CustomPopover
              content={
                <HexColorPicker
                  style={{ margin: "20px" }}
                  color={chartFiltersFormState.chartColor}
                  onChange={onChangeColor}
                />
              }
            >
              <Button color={chartFiltersFormState.chartColor}>
                <SquareIcon />
              </Button>
            </CustomPopover>
          }
        ></TextField>
        <Flex justifyContent="space-between">
          <ReplayCircleFilled
            onClick={() => onAutoRefreshAll(!isAutoRefreshAll)}
            style={styles.iconAction}
            color={!isAutoRefreshAll ? "action" : "primary"}
          ></ReplayCircleFilled>
          <Button
            style={styles.buttonUpdate}
            children={strings.fields.buttons.update}
            onClick={onSubmitForm}
            variation="primary"
            isDisabled={Object.values(errors).some((e) => !!e)}
          ></Button>
        </Flex>
      </Grid>
      <CModal
        alignment="center"
        visible={modalDateVisible}
        onClose={() => setModalDateVisible(false)}
        portal={false}
        backdrop="static"
      >
        <CModalHeader>
          <CModalTitle>{strings.selectDateRange}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <DateRangeForm
            onSubmit={({ from, to }) => onSubmitDateRange(from, to)}
          />
        </CModalBody>
      </CModal>
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    fields: {
      period: {
        label: "Periodo",
      },
      scheduleType: {
        label: "Tipo de Horario",
      },
      chartColor: {
        label: "Color de Gráfica",
      },
      buttons: {
        update: "Actualizar",
      },
    },
    selectOption: "Seleccione una opción",
    selectDateRange: "Seleccionar Rango de Fecha",
    selectColor: "Seleccionar Color",
  },
});

const styles = {
  buttonUpdate: {
    height: "42px",
    alignSelf: "self-end",
  },
  iconAction: {
    cursor: "pointer",
    fontSize: "30px",
    alignSelf: "self-end",
    marginBottom: "5px",
  },
};

export default ChartFiltersForm;
